<template>
  <!-- Bolt Fleet API 2.0 w karcie kierowcy (29.10.2026): zawieszenie, oceny, auto z Bolta. -->
  <div v-if="data && data.configured" class="bolt-quality">
    <v-alert v-if="data.suspended" type="error" prominent dense class="mb-3" data-test="bolt-suspended">
      <div class="font-weight-bold">
        {{ $t('boltQuality.suspendedTitle') }}
        <span v-if="data.suspendedSince">— {{ $t('boltQuality.suspendedSince', { date: fmt(data.suspendedSince) }) }}</span>
      </div>
      <div v-for="(z, i) in data.suspensions" :key="i" class="mt-1">
        {{ z.label }}<span v-if="z.message"> — „{{ z.message }}”</span>
      </div>
    </v-alert>

    <v-alert v-if="data.carProposal" type="warning" dense outlined class="mb-3" data-test="bolt-car">
      <div class="font-weight-bold">{{ $t('boltQuality.carTitle') }}</div>
      <div>
        {{
          $t('boltQuality.carText', {
            bolt: data.carProposal.boltCar.plate,
            ours: data.carProposal.ourCar ? data.carProposal.ourCar.plate : $t('boltQuality.carNone'),
          })
        }}
      </div>
      <div v-if="data.carProposal.companyCar" class="text-caption mt-1">{{ $t('boltQuality.carCompanyNote') }}</div>
      <v-btn small color="warning" class="mt-2" :loading="assigning" @click="assignCar">
        {{ $t('boltQuality.carAssign', { plate: data.carProposal.boltCar.plate }) }}
      </v-btn>
    </v-alert>

    <v-card outlined class="mb-3">
      <v-card-title class="text-subtitle-1 py-2">
        <v-icon small class="mr-2">mdi-star-circle-outline</v-icon>
        {{ $t('boltQuality.title') }} — {{ $t('boltQuality.ratingsTitle') }}
        <v-chip v-if="r.declining" x-small color="error" class="ml-2">{{ $t('boltQuality.declining.' + r.declining) }}</v-chip>
        <v-spacer />
        <span class="text-caption grey--text">
          <span v-if="data.boltStatus">{{ $t('boltQuality.boltStatus.' + data.boltStatus) }} · </span>
          {{ $t('boltQuality.checkedAt', { date: fmt(data.checkedAt, true) }) }}
        </span>
      </v-card-title>
      <v-card-text class="pt-0">
        <v-row dense>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">{{ $t('boltQuality.rating') }}</div>
            <div class="text-h6" data-test="bolt-rating">
              {{ r.last30.srednia !== null ? r.last30.srednia.toFixed(2) : '—' }}
            </div>
            <div class="text-caption">
              {{ r.last30.ocen ? $t('boltQuality.ratingCount', { count: r.last30.ocen }) : $t('boltQuality.noRatings') }}
            </div>
            <div v-if="r.previous30.srednia !== null" class="text-caption grey--text">
              {{ $t('boltQuality.previous', { value: r.previous30.srednia.toFixed(2) }) }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">{{ $t('boltQuality.finished') }}</div>
            <div class="text-h6">{{ r.last30.zakonczone }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">{{ $t('boltQuality.driverCancelled') }}</div>
            <div class="text-h6">{{ r.last30.anulowaneKierowca }}</div>
            <div v-for="(n, powod) in r.last30.powodyKierowcy" :key="powod" class="text-caption">
              {{ reason(powod) }}: {{ n }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">{{ $t('boltQuality.riderCancelled') }}</div>
            <div class="text-h6">{{ r.last30.anulowanePasazer }}</div>
            <div class="text-caption">{{ $t('boltQuality.noShow') }}: {{ r.last30.pasazerNieprzyszedl }}</div>
          </v-col>
        </v-row>
        <div v-if="data.autoAssignedAt" class="text-caption grey--text mt-1">
          {{ $t('boltQuality.carAuto', { date: fmt(data.autoAssignedAt, true) }) }}
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
const axios = require('axios');

export default {
  name: 'BoltDriverQuality',
  props: { driverId: { type: String, required: true } },
  data: () => ({ data: null, assigning: false }),
  computed: {
    r() {
      return this.data.ratings;
    },
  },
  watch: {
    driverId: { immediate: true, handler: 'load' },
  },
  methods: {
    headers() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    async load() {
      if (!this.driverId) return;
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'bolt/quality/driver/' + this.driverId, {
          headers: this.headers(),
        });
        this.data = res.data.data;
      } catch (e) {
        // Brak uprawnień / brak klucza — karta kierowcy działa bez tej sekcji.
        this.data = null;
      }
    },
    reason(code) {
      const k = 'boltQuality.reasons.' + code;
      return this.$te(k) ? this.$t(k) : code;
    },
    fmt(d, czas = false) {
      if (!d) return '—';
      const x = new Date(d);
      return czas ? x.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : x.toLocaleDateString('pl-PL');
    },
    async assignCar() {
      const auto = this.data.carProposal.boltCar;
      this.assigning = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'cars/' + auto.id + '/assign-driver',
          { driverId: this.driverId },
          { headers: this.headers() },
        );
        this.$emit('assigned', auto);
        await this.load();
      } catch (e) {
        this.$emit('error', (e.response && e.response.data && e.response.data.message) || e.message);
      } finally {
        this.assigning = false;
      }
    },
  },
};
</script>
